'use client';

import './pagination.scss';

import dynamic from 'next/dynamic';
import type { RefObject } from 'react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';

import { usePaginationQueryParams } from '../../utils/query-params.tsx';

const ReactPaginate = dynamic(() => import('react-paginate'), { ssr: false });

const scrollOffset = 120;

const scrollToTarget = (ref?: RefObject<HTMLElement | null>) => {
  if (ref?.current) {
    const top =
      ref.current.getBoundingClientRect().top +
      globalThis.scrollY -
      scrollOffset;
    globalThis.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  } else {
    globalThis.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

export const PaginationProps = z.object({
  totalItems: z.number().min(1),
  itemsPerPage: z.number().min(1),
  initialPage: z.number().min(1).optional(),
});
export type PaginationProps = z.infer<typeof PaginationProps> & {
  onPageChange?: (page: number) => void;
  /** Element to scroll to on page change. Falls back to top of page. */
  scrollRef?: RefObject<HTMLElement | null>;
};

const InternalPagination: React.FC<PaginationProps> = ({
  itemsPerPage,
  totalItems,
  onPageChange,
  scrollRef,
}) => {
  const { currentPage, setCurrentPage } = usePaginationQueryParams({
    history: scrollRef ? 'replace' : 'push',
  });
  const { t } = useTranslation(['articles']);

  const pageCount = Math.ceil(totalItems / itemsPerPage);

  const handlePageClick = (activePage: number) => {
    const cleanedActivePage = Number.isNaN(activePage) ? 0 : activePage;
    if (currentPage === cleanedActivePage) {
      return;
    }
    setCurrentPage(activePage);
    onPageChange?.(cleanedActivePage);
    scrollToTarget(scrollRef);
  };

  const getPrevButton = () => (
    <button
      className="Pagination__adjacent Pagination__adjacent--prev label-potent label-md"
      type="button"
      disabled={currentPage === 0}
      onClick={() => handlePageClick(currentPage - 1)}
    >
      <ArkIcon iconName="arrow-left" size="20" />
      <span>{t('articles:pagination.previous')}</span>
    </button>
  );

  const getNextButton = () => (
    <button
      className="Pagination__adjacent Pagination__adjacent--next label-potent label-md"
      type="button"
      disabled={currentPage === pageCount - 1}
      onClick={() => handlePageClick(currentPage + 1)}
    >
      <span>{t('articles:pagination.next')}</span>
      <ArkIcon iconName="arrow-right" size="20" />
    </button>
  );

  return (
    <div className="Pagination">
      <ReactPaginate
        breakLabel="..."
        breakClassName="text-center"
        nextLabel={getNextButton()}
        nextLinkClassName="Pagination__adjacent-link Pagination__adjacent-link--next label-potent label-md"
        nextClassName="Pagination__adjacent Pagination__adjacent--next"
        onPageChange={(v) => handlePageClick(v.selected)}
        pageRangeDisplayed={2}
        marginPagesDisplayed={1}
        pageCount={pageCount}
        forcePage={currentPage}
        previousLabel={getPrevButton()}
        renderOnZeroPageCount={null}
        containerClassName="Pagination__list"
        previousClassName="Pagination__adjacent Pagination__adjacent--prev"
        previousLinkClassName="Pagination__adjacent-link Pagination__adjacent-link--prev label-potent label-md"
        pageClassName="Pagination__page"
        pageLinkClassName="ButtonStyle"
      />
    </div>
  );
};

const PaginationSkeleton = () => (
  <div className="Pagination">
    <div className="Pagination__list">
      <div className="Pagination__adjacent Pagination__adjacent--prev">
        <Skeleton height={20} width={20} borderRadius="50%" />
        <span>
          <Skeleton height={16} width={60} />
        </span>
      </div>

      <div className="flex gap-snug-sm">
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
        <Skeleton height={48} width={48} borderRadius="0.5rem" />
      </div>

      <div className="Pagination__adjacent Pagination__adjacent--next">
        <span>
          <Skeleton height={16} width={40} />
        </span>
        <Skeleton height={20} width={20} borderRadius="50%" />
      </div>
    </div>
  </div>
);

export const Pagination: React.FC<PaginationProps> = (props) => (
  <Suspense fallback={<PaginationSkeleton />}>
    <InternalPagination {...props} />
  </Suspense>
);
